import React, { useMemo, useState } from 'react'
import { Typography, Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'

import Tree from '../Tree'
import SearchNoAccountFound from './SearchNoAccountFound'
import SearchEmptySelection from './SearchEmptySelection'
import SearchProvider, { useSearchContext } from './SearchProvider'
import convertAccounts from '../../utils'
import { useTreeContext, useSitesSelected } from '../../TreeProvider'
import { useInPerimeter } from '../../utils'
import Loader from 'components/Loader'
import TopBar from '../TopBar/TopBar'
import StackSelection from '../StackSelection'

const OPERATION_SEARCH_ACCOUNTS = gql`
    query SearchAccount ($name: String!) {
        accounts: accountsByName(name: $name) {
            id
            name
            highlight_name
            sf_id
            sf_parent_id
            in_perimeter
            hierarchie
            is_site
            nb_children
            has_no_production_sites
            should_display_count
            has_sites
            state
        }
    }
`

const Search = React.forwardRef(({ tree, highlightWord, onlySite, onNodeSelect, height = 400, hideTopBar = false }) => {
  const [activeTab, setActiveTab] = useState(0)
  const checkedAccounts = useInPerimeter()
  const { onNodeAdd, onNodeRemove, waiting, selection, treeRef } = useTreeContext()
  const { apiFold } = useSearchContext()
  const { sites: sitesSelected } = useSitesSelected({ selection })

  if (activeTab === 1) {
    return (
      <Stack pt={1}>
        <TopBar activeTab={activeTab} onTabChange={setActiveTab} />
        {sitesSelected.length === 0 ? (
          <SearchEmptySelection />
        ) : (
          <StackSelection
            onRemoveAccount={(account) => {
              onNodeRemove({ id: account.sf_id })
            }}
            accounts={sitesSelected}
            treeRef={treeRef}
          />
        )}
      </Stack>
    )
  }

  if (!tree.length) {
    return <SearchNoAccountFound />
  }

  return (
    <Stack pt={hideTopBar ? 0 : 1}>
      {!hideTopBar && <TopBar activeTab={activeTab} onTabChange={setActiveTab} />}
      <Box sx={{ position: 'relative' }}>
        <Tree
          ref={treeRef}
          data={tree}
          highlightWord={highlightWord}
          apiFold={apiFold}
          openByDefault={(node) => node.children.length <= 1}
          onlySite={onlySite}
          height={height}
          onNodeSelect={async (node) => {
            const checked = checkedAccounts.findIndex(account => account.sf_id === node.data.data.sf_id) > -1
            if (!checked) {
              if (!waiting) {
                await onNodeAdd(node)
              }
            } else {
              onNodeRemove(node)
            }
            onNodeSelect(node, !checked)
          }}
          disableChildOnParentSelect
        />
      </Box>
    </Stack>
  )
})

const SearchLoader = React.forwardRef(({ name, view, onlySite = false, onNodeSelect = () => { }, height, hideTopBar = false }) => {
  const { apiFold } = useSearchContext()
  const { t } = useTranslation()
  const { data, loading, error } = useQuery(OPERATION_SEARCH_ACCOUNTS, {
    variables: {
      name
    }
  })

  const tree = useMemo(() => {
    if (!data) {
      return []
    }
    const accounts = [...data.accounts, ...apiFold.accounts.filter(account =>
      data.accounts.findIndex(({ sf_id }) => sf_id === account.sf_id) === -1)]
    return convertAccounts({ accounts })
  }, [data, apiFold.accounts])

  if (loading) {
    return (
      <Box mt={2}>
        <Loader size={24} />
      </Box>
    )
  }

  if (error) {
    return <Typography variant={'caption'}>{t('auth.admin.sites.search_error')}</Typography>
  }

  return (
    <Search
      tree={tree}
      onlySite={onlySite}
      highlightWord={name}
      view={view}
      height={height}
      hideTopBar={hideTopBar}
      onNodeSelect={onNodeSelect}
    />
  )
})

export default React.forwardRef(({ name, height, ...props }) => (
  <SearchProvider name={name}>
    <SearchLoader {...props} name={name} height={height} />
  </SearchProvider>
))
